import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LinkIcon, ArrowRightIcon, CheckIcon } from '@heroicons/react/24/outline';

const AnalyzePropertyImport = () => {
    const navigate = useNavigate();
    const { id } = useParams(); // UUID public_id

    const [numericId, setNumericId] = useState(null);
    const [resolving, setResolving] = useState(true);

    // URL input
    const [url, setUrl] = useState('');
    const [urlError, setUrlError] = useState(null);

    // Estados de carga
    const [fetching, setFetching] = useState(false);   // consultando API
    const [importing, setImporting] = useState(false); // confirmando importación
    const [fetchError, setFetchError] = useState(null);

    // Datos traídos de Idealista
    const [preview, setPreview] = useState(null); // datos normalizados

    // Imágenes seleccionadas (conjunto de índices)
    const [selectedFotos, setSelectedFotos] = useState(new Set());

    // Resolver UUID → numericId al montar
    useEffect(() => {
        const resolve = async () => {
            try {
                const res = await fetch(
                    `${window.REACT_APP_API_ENDPOINT}/api/wizard/propiedades/by-public-id/${id}`,
                    { credentials: 'include' }
                );
                if (!res.ok) throw new Error();
                const data = await res.json();
                setNumericId(data.id);
            } catch {
                // Si falla, el usuario verá el error al intentar importar
            } finally {
                setResolving(false);
            }
        };
        resolve();
    }, [id]);

    const handleFetch = async (e) => {
        e.preventDefault();
        setUrlError(null);
        setFetchError(null);

        if (!url.includes('idealista.com/inmueble/')) {
            setUrlError('Introduce una URL válida de Idealista. Ejemplo: https://www.idealista.com/inmueble/12345678/');
            return;
        }

        setFetching(true);
        try {
            const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/api/wizard/idealista/from-url`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ url }),
            });
            const json = await res.json();
            if (!json.ok) throw new Error(json.error || 'Error al consultar Idealista');

            setPreview(json.data);
            // Seleccionar las primeras 10 fotos por defecto
            setSelectedFotos(new Set(json.data.fotos.slice(0, 10).map((_, i) => i)));
        } catch (err) {
            setFetchError(err.message);
        } finally {
            setFetching(false);
        }
    };

    const toggleFoto = (idx) => {
        setSelectedFotos(prev => {
            const next = new Set(prev);
            next.has(idx) ? next.delete(idx) : next.add(idx);
            return next;
        });
    };

    const handleConfirm = async () => {
        if (!numericId || !preview) return;
        setImporting(true);
        setFetchError(null);
        try {
            const fotosSeleccionadas = preview.fotos.filter((_, i) => selectedFotos.has(i));

            const res = await fetch(
                `${window.REACT_APP_API_ENDPOINT}/api/wizard/idealista/import/${numericId}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({
                        datos: preview,
                        fotos: fotosSeleccionadas,
                    }),
                }
            );
            const json = await res.json();
            if (!json.ok) throw new Error('Error al importar los datos');

            if (json.totalFotos > 0) {
                localStorage.setItem(`idealista_import_${id}`, JSON.stringify({ total: json.totalFotos }));
            }
            navigate(`/analizarpropiedad/${id}/paso1`);
        } catch (err) {
            setFetchError(err.message);
            setImporting(false);
        }
    };

    const handleSkip = () => navigate(`/analizarpropiedad/${id}/paso1`);

    if (resolving) {
        return <div className="max-w-2xl mx-auto px-4 py-8 text-center text-gray-500">Cargando...</div>;
    }

    return (
        <div className="max-w-2xl mx-auto px-4 py-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8">

                {/* Cabecera */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-50 rounded-full mb-4">
                        <LinkIcon className="w-6 h-6 text-blue-600" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                        ¿Tienes el anuncio en Idealista?
                    </h1>
                    <p className="text-gray-500 text-sm">
                        Pega la URL y rellenaremos automáticamente los datos e importaremos las fotos.
                    </p>
                </div>

                {/* Formulario URL */}
                {!preview && (
                    <form onSubmit={handleFetch} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                URL del anuncio
                            </label>
                            <input
                                type="url"
                                value={url}
                                onChange={e => setUrl(e.target.value)}
                                placeholder="https://www.idealista.com/inmueble/12345678/"
                                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            {urlError && (
                                <p className="mt-1 text-xs text-red-600">{urlError}</p>
                            )}
                        </div>

                        {fetchError && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                                {fetchError}
                            </div>
                        )}

                        <div className="flex items-center gap-3 pt-2">
                            <button
                                type="submit"
                                disabled={fetching || !url}
                                className="flex-1 inline-flex items-center justify-center py-2.5 px-6 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150"
                            >
                                {fetching ? 'Consultando Idealista...' : 'Importar datos'}
                            </button>
                            <button
                                type="button"
                                onClick={handleSkip}
                                className="text-sm text-gray-500 hover:text-gray-700 underline"
                            >
                                Saltar →
                            </button>
                        </div>
                    </form>
                )}

                {/* Preview de datos importados */}
                {preview && (
                    <div className="space-y-6">

                        {/* Datos básicos */}
                        <div>
                            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                                Datos importados
                            </h2>
                            <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-3 text-sm">
                                {preview.titulo && (
                                    <div className="col-span-2">
                                        <span className="text-gray-500">Título</span>
                                        <p className="font-medium text-gray-900">{preview.titulo}</p>
                                    </div>
                                )}
                                {preview.precio != null && (
                                    <div>
                                        <span className="text-gray-500">Precio</span>
                                        <p className="font-medium text-gray-900">{preview.precio.toLocaleString('es-ES')} €</p>
                                    </div>
                                )}
                                {preview.superficie != null && (
                                    <div>
                                        <span className="text-gray-500">Superficie</span>
                                        <p className="font-medium text-gray-900">{preview.superficie} m²</p>
                                    </div>
                                )}
                                {preview.habitaciones != null && (
                                    <div>
                                        <span className="text-gray-500">Habitaciones</span>
                                        <p className="font-medium text-gray-900">{preview.habitaciones}</p>
                                    </div>
                                )}
                                {preview.banos != null && (
                                    <div>
                                        <span className="text-gray-500">Baños</span>
                                        <p className="font-medium text-gray-900">{preview.banos}</p>
                                    </div>
                                )}
                                {preview.municipio && (
                                    <div className="col-span-2">
                                        <span className="text-gray-500">Ubicación aproximada</span>
                                        <p className="font-medium text-gray-900">
                                            {preview.distrito ? `${preview.distrito}, ` : ''}{preview.municipio}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {preview.direccion_oculta && (
                                <p className="mt-2 text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
                                    El anuncio no incluye la dirección exacta. En el siguiente paso podrás introducirla para obtener datos catastrales.
                                </p>
                            )}
                        </div>

                        {/* Selección de fotos */}
                        {preview.fotos.length > 0 && (
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                                        Fotos ({selectedFotos.size} de {preview.fotos.length} seleccionadas)
                                    </h2>
                                    <div className="flex gap-2 text-xs">
                                        <button
                                            type="button"
                                            onClick={() => setSelectedFotos(new Set(preview.fotos.map((_, i) => i)))}
                                            className="text-blue-600 hover:underline"
                                        >
                                            Todas
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedFotos(new Set())}
                                            className="text-gray-500 hover:underline"
                                        >
                                            Ninguna
                                        </button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-72 overflow-y-auto pr-1">
                                    {preview.fotos.map((fotoUrl, idx) => (
                                        <button
                                            key={idx}
                                            type="button"
                                            onClick={() => toggleFoto(idx)}
                                            className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                                                selectedFotos.has(idx)
                                                    ? 'border-blue-500 ring-2 ring-blue-300'
                                                    : 'border-gray-200 opacity-60'
                                            }`}
                                        >
                                            <img
                                                src={fotoUrl}
                                                alt={`Foto ${idx + 1}`}
                                                className="w-full h-full object-cover"
                                            />
                                            {selectedFotos.has(idx) && (
                                                <div className="absolute top-1 right-1 bg-blue-500 rounded-full p-0.5">
                                                    <CheckIcon className="w-3 h-3 text-white" />
                                                </div>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {fetchError && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                                {fetchError}
                            </div>
                        )}

                        {/* Acciones */}
                        <div className="flex items-center gap-3 pt-2">
                            <button
                                type="button"
                                onClick={handleConfirm}
                                disabled={importing}
                                className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 px-6 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150"
                            >
                                {importing ? 'Importando...' : (
                                    <>Confirmar e importar <ArrowRightIcon className="w-4 h-4" /></>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => setPreview(null)}
                                className="text-sm text-gray-500 hover:text-gray-700 underline"
                            >
                                ← Cambiar URL
                            </button>
                        </div>
                        <div className="text-center">
                            <button
                                type="button"
                                onClick={handleSkip}
                                className="text-xs text-gray-400 hover:text-gray-600 underline"
                            >
                                Saltar importación e introducir datos manualmente
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AnalyzePropertyImport;
